import { useEffect, useRef, useState } from "react";

type UseDelayedVisibleOptions = {
    /** active になってから表示に切り替えるまでの遅延 (ms)。この時間内に active が false に戻れば一度も表示しない */
    delayMs: number;
    /** 一度表示したら最低これだけは表示し続ける (ms)。閾値をわずかに超えて応答した場合の点滅を防ぐ */
    minVisibleMs: number;
};

// 「短時間で終わる処理では出さず、長引いたときだけ出す」表示制御。
// 処理中オーバーレイのように、毎回出るとうるさく、出たときには「本当に待たせている」と伝えたい UI に使う。
// 表示可否だけを返す純粋な hook で、何を表示するかは呼び出し側が決める。
export const useDelayedVisible = (isActive: boolean, { delayMs, minVisibleMs }: UseDelayedVisibleOptions): boolean => {
    const [isVisible, setIsVisible] = useState(false);
    const shownAtRef = useRef<number | null>(null);

    useEffect(() => {
        if (isActive) {
            if (shownAtRef.current !== null) return;
            const timer = setTimeout(() => {
                shownAtRef.current = Date.now();
                setIsVisible(true);
            }, delayMs);
            return () => clearTimeout(timer);
        }

        // 非 active 化: 未表示なら何もしない (timer は上の cleanup で解除済み)。表示済みなら最短表示時間を守って消す
        if (shownAtRef.current === null) return;
        const elapsed = Date.now() - shownAtRef.current;
        const hold = Math.max(0, minVisibleMs - elapsed);
        const timer = setTimeout(() => {
            shownAtRef.current = null;
            setIsVisible(false);
        }, hold);
        return () => clearTimeout(timer);
    }, [isActive, delayMs, minVisibleMs]);

    return isVisible;
};
